import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, X, ArrowRight, TrendingDown, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

interface CostAlert {
  id: number;
  restaurant_id: number;
  recipe_id: number;
  ingredient_id: number | null;
  type: 'status_change' | 'price_impact' | 'spike';
  old_status: string | null;
  new_status: string | null;
  old_food_cost: number | null;
  new_food_cost: number | null;
  price_change_pct: number | null;
  message: string;
  is_read: boolean;
  is_dismissed: boolean;
  created_at: string;
  recipe?: { id: number; name: string };
  ingredient?: { id: number; name: string };
}

interface CostAlertsBannerProps {
  restaurantId: number | null;
}

export default function CostAlertsBanner({ restaurantId }: CostAlertsBannerProps) {
  const [alerts, setAlerts] = useState<CostAlert[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  const fetchAlerts = useCallback(async () => {
    if (!restaurantId) return;
    try {
      const [countRes, alertsRes] = await Promise.all([
        api.get('/cost-alerts/count'),
        api.get('/cost-alerts', { params: { per_page: 5 } }),
      ]);
      setUnreadCount(countRes.data.unread ?? 0);
      setAlerts(alertsRes.data.data ?? []);
    } catch {
      // Silently fail — the banner just won't show
    } finally {
      setIsLoading(false);
    }
  }, [restaurantId]);

  useEffect(() => {
    fetchAlerts();
  }, [fetchAlerts]);

  const dismissAlert = async (alertId: number) => {
    try {
      await api.post(`/cost-alerts/${alertId}/dismiss`);
      setAlerts(prev => prev.filter(a => a.id !== alertId));
      setUnreadCount(prev => Math.max(0, prev - 1));
      toast.success('Alerta descartada');
    } catch {
      toast.error('Error al descartar la alerta');
    }
  };

  const dismissAll = async () => {
    try {
      await api.post('/cost-alerts/dismiss-all');
      setAlerts([]);
      setUnreadCount(0);
      toast.success('Todas las alertas descartadas');
    } catch {
      toast.error('Error al descartar las alertas');
    }
  };

  // Don't render anything while loading or if there are no alerts
  if (isLoading || unreadCount === 0 || alerts.length === 0) {
    return null;
  }

  // Determine severity: red if any alert transitions to 'red', amber otherwise
  const hasRedAlert = alerts.some(a => a.new_status === 'red');
  const bannerBg = hasRedAlert ? 'bg-red-50 border-red-200' : 'bg-amber-50 border-amber-200';
  const bannerIconColor = hasRedAlert ? 'text-red-500' : 'text-amber-500';
  const bannerTitleColor = hasRedAlert ? 'text-red-800' : 'text-amber-800';
  const bannerDismissColor = hasRedAlert ? 'text-red-500 hover:text-red-700' : 'text-amber-500 hover:text-amber-700';
  const itemBorder = hasRedAlert ? 'border-red-100' : 'border-amber-100';

  const getAlertIcon = (alert: CostAlert) => {
    if (alert.type === 'spike' || (alert.price_change_pct && alert.price_change_pct > 0)) {
      return <TrendingUp className="w-4 h-4 text-amber-500 flex-shrink-0" />;
    }
    return <TrendingDown className="w-4 h-4 text-red-500 flex-shrink-0" />;
  };

  const getStatusBadge = (status: string | null) => {
    switch (status) {
      case 'green':
        return <span className="inline-block px-1.5 py-0.5 text-[10px] font-semibold rounded bg-green-100 text-green-700">Rentable</span>;
      case 'yellow':
        return <span className="inline-block px-1.5 py-0.5 text-[10px] font-semibold rounded bg-amber-100 text-amber-700">Ajustada</span>;
      case 'red':
        return <span className="inline-block px-1.5 py-0.5 text-[10px] font-semibold rounded bg-red-100 text-red-700">No rentable</span>;
      default:
        return null;
    }
  };

  return (
    <div className={`${bannerBg} border rounded-xl p-4 mb-6`}>
      <div className="flex items-center gap-3 mb-3">
        <AlertTriangle className={`w-5 h-5 ${bannerIconColor} flex-shrink-0`} />
        <h3 className={`font-semibold ${bannerTitleColor}`}>
          {unreadCount} {unreadCount === 1 ? 'receta ha perdido' : 'recetas han perdido'} rentabilidad
        </h3>
        <button
          onClick={dismissAll}
          className={`ml-auto text-xs font-medium ${bannerDismissColor} transition-colors`}
        >
          Descartar todo
        </button>
      </div>
      <div className="space-y-2">
        {alerts.map(alert => (
          <div
            key={alert.id}
            className={`flex items-center justify-between bg-white rounded-lg p-3 border ${itemBorder} gap-3`}
          >
            <div className="flex items-center gap-2.5 min-w-0 flex-1">
              {getAlertIcon(alert)}
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-1.5">
                  <span className="font-medium text-slate-900 text-sm truncate">
                    {alert.recipe?.name ?? `Receta #${alert.recipe_id}`}
                  </span>
                  {alert.old_status && alert.new_status && (
                    <span className="flex items-center gap-1">
                      {getStatusBadge(alert.old_status)}
                      <ArrowRight className="w-3 h-3 text-slate-400" />
                      {getStatusBadge(alert.new_status)}
                    </span>
                  )}
                </div>
                <p className="text-xs text-slate-500 mt-0.5 truncate">{alert.message}</p>
                {alert.old_food_cost != null && alert.new_food_cost != null && (
                  <p className="text-xs text-slate-400 mt-0.5">
                    Food cost: {alert.old_food_cost.toFixed(1)}% → {alert.new_food_cost.toFixed(1)}%
                    {alert.ingredient?.name && (
                      <span className="ml-1">(por {alert.ingredient.name})</span>
                    )}
                  </p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <Link
                to={`/recipes/${alert.recipe_id}`}
                className="text-xs text-blue-500 hover:text-blue-600 font-medium whitespace-nowrap"
              >
                Ver receta
              </Link>
              <button
                onClick={() => dismissAlert(alert.id)}
                className="text-slate-400 hover:text-slate-600 transition-colors p-0.5"
                title="Descartar"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Hook to fetch the unread cost alerts count.
 * Used by the Sidebar to show a red dot badge.
 */
export function useCostAlertCount(): number {
  const [count, setCount] = useState(0);

  useEffect(() => {
    let cancelled = false;
    api.get('/cost-alerts/count')
      .then(res => {
        if (!cancelled) setCount(res.data.unread ?? 0);
      })
      .catch(() => {
        // ignore
      });
    return () => { cancelled = true; };
  }, []);

  return count;
}
